import React, { useState } from "react";
import { http, formatApiError } from "../resources/resourceUtils";

const Field = ({ id, label, ...props }) => (
    <div>
        <label htmlFor={id} className="block text-[15px] mb-2" style={{ color: "#4A4A30" }}>{label}</label>
        <input id={id} className="res-input !pl-5 !rounded-xl" {...props} />
    </div>
);

const Shell = ({ title, children, testid }) => (
    <div className="res-card mx-auto max-w-md p-10" data-testid={testid}>
        <span className="res-eyebrow">Alchemy Practice</span>
        <h1 className="mt-3 font-serif text-[34px]" style={{ color: "#4A4A30" }}>{title}</h1>
        <div className="mt-8">{children}</div>
    </div>
);

const Msg = ({ error, ok }) =>
    error ? <p role="alert" className="text-[15px]" style={{ color: "#9A4557" }} data-testid="admin-auth-error">{error}</p>
        : ok ? <p role="status" className="text-[15px]" style={{ color: "#5E6748" }} data-testid="admin-auth-ok">{ok}</p> : null;

function useSubmit(fn) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [ok, setOk] = useState("");
    const run = async (e) => {
        e.preventDefault();
        setBusy(true); setError(""); setOk("");
        try { setOk((await fn()) || ""); } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
    };
    return { busy, error, ok, run };
}

export const LoginForm = ({ onLoggedIn, onForgot, notice }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const { busy, error, run } = useSubmit(async () => {
        const { data } = await http.post("/admin-auth/login", { email, password });
        onLoggedIn(data);
    });
    return (
        <Shell title="Resources admin" testid="admin-login">
            <form onSubmit={run} className="space-y-5">
                {notice && <p className="text-[15px]" style={{ color: "#5E6748" }} data-testid="admin-login-notice">{notice}</p>}
                <Field id="admin-email" label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email" />
                <Field id="admin-password" label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password" />
                <Msg error={error} />
                <button type="submit" disabled={busy} className="res-btn w-full justify-center disabled:opacity-60" data-testid="admin-login-submit">
                    {busy ? "Signing in..." : "Sign in"}
                </button>
                <button type="button" onClick={onForgot} className="res-link block mx-auto" data-testid="admin-forgot-link">Forgot your password?</button>
            </form>
        </Shell>
    );
};

export const ForgotForm = ({ onBack }) => {
    const [email, setEmail] = useState("");
    const { busy, error, ok, run } = useSubmit(async () => {
        await http.post("/admin-auth/forgot-password", { email });
        return "If that email belongs to the admin account, a reset link is on its way. It works for one hour.";
    });
    return (
        <Shell title="Reset your password" testid="admin-forgot">
            <form onSubmit={run} className="space-y-5">
                <Field id="forgot-email" label="Admin email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-forgot-email" />
                <Msg error={error} ok={ok} />
                <button type="submit" disabled={busy} className="res-btn w-full justify-center disabled:opacity-60" data-testid="admin-forgot-submit">
                    {busy ? "Sending..." : "Email me a reset link"}
                </button>
                <button type="button" onClick={onBack} className="res-link block mx-auto" data-testid="admin-forgot-back">Back to sign in</button>
            </form>
        </Shell>
    );
};

export const ResetForm = ({ token, onDone }) => {
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const { busy, error, run } = useSubmit(async () => {
        if (pw !== pw2) throw new Error("The two passwords do not match.");
        await http.post("/admin-auth/reset-password", { token, password: pw });
        onDone();
    });
    return (
        <Shell title="Choose a new password" testid="admin-reset">
            <form onSubmit={run} className="space-y-5">
                <Field id="reset-pw" label="New password (at least 10 characters)" type="password" autoComplete="new-password" minLength={10} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="admin-reset-password" />
                <Field id="reset-pw2" label="Confirm new password" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="admin-reset-password-confirm" />
                <Msg error={error} />
                <button type="submit" disabled={busy} className="res-btn w-full justify-center disabled:opacity-60" data-testid="admin-reset-submit">
                    {busy ? "Saving..." : "Save new password"}
                </button>
            </form>
        </Shell>
    );
};
